import { zodResolver } from '@hookform/resolvers/zod';
import {
  I18nOptionsAutocompleteFieldWithController,
  TextAreaFieldWithController,
  YesNoRadioFieldWithController,
} from '@procureai/ui-form-mui';
import { PropsWithChildren, ReactNode, useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  GkgNoMeasureValues,
  GkgYesMeasureValues,
  IntakeNuclearComplianceFormStepData,
  IntakeNuclearComplianceFormStepInput,
  IntakeNuclearComplianceFormStepDefaultValues,
  intakeNuclearComplianceFormStepFormSchema,
  KkbClarificationValues,
  NdaAvailabilityValues,
  OrderTypeValues,
  QGroupValues,
} from '../IntakeNuclearComplianceFormStep.schema';
import { I18nNamespace } from '@/legacy/common/types';

import { getResetGkgValue, getResetNdaValue } from './IntakeNuclearComplianceForm.reset';

export type IntakeNuclearComplianceFormProps = {
  formId: string;
  data: IntakeNuclearComplianceFormStepData | null;
  onValidityChange?: (isValid: boolean) => void;

  onSubmit: (values: IntakeNuclearComplianceFormStepInput) => Promise<void> | void;
};

export const IntakeNuclearComplianceForm = ({
  formId,
  data,
  onValidityChange,
  onSubmit,
}: IntakeNuclearComplianceFormProps): ReactNode => {
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: 'IntakeNuclearComplianceForm' });

  const form = useForm<IntakeNuclearComplianceFormStepInput>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: zodResolver(intakeNuclearComplianceFormStepFormSchema),
    defaultValues: data ?? IntakeNuclearComplianceFormStepDefaultValues,
  });

  const { watch, setValue } = form;

  // watch the gkg field to conditionally show/hide fields
  const [gkgRequired, ndaRequired, kkbApplicable, kkbHazardous] = watch([
    'gkg.required',
    'nda.required',
    'kkb.applicable',
    'kkb.hazardous',
  ]);

  const prevGkgRequired = usePrevious(gkgRequired);
  const prevNdaRequired = usePrevious(ndaRequired);
  const prevKkbApplicable = usePrevious(kkbApplicable);
  const prevKkbHazardous = usePrevious(kkbHazardous);

  useEffect(() => {
    onValidityChange?.(form.formState.isValid);
  }, [form.formState.isValid, onValidityChange]);

  // clear any previously entered gkg dependent values.
  useEffect(() => {
    if (prevGkgRequired === undefined) return;
    if (prevGkgRequired === gkgRequired) return;

    setValue('gkg', getResetGkgValue(gkgRequired), {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [setValue, gkgRequired, prevGkgRequired]);

  // clear any previously entered nda dependent values.
  useEffect(() => {
    if (prevNdaRequired === undefined) return;
    if (prevNdaRequired === ndaRequired) return;

    setValue('nda', getResetNdaValue(ndaRequired), {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [setValue, ndaRequired, prevNdaRequired]);

  // clear any previously entered kkb dependent values.
  useEffect(() => {
    if (prevKkbApplicable === undefined) return;
    if (prevKkbApplicable === kkbApplicable) return;

    setValue(
      'kkb',
      kkbApplicable
        ? {
            applicable: true,
            hazardous: null,
            controlledZone: null,
            tenuFourZone: null,
            clarification: null,
            comment: '',
          }
        : { applicable: false, hazardous: false },
      {
        shouldDirty: true,
        shouldValidate: true,
      },
    );
  }, [setValue, kkbApplicable, prevKkbApplicable]);

  // clear any previously entered kkb dependent values.
  useEffect(() => {
    if (prevKkbHazardous === undefined) return;
    if (prevKkbHazardous === kkbHazardous) return;

    // set or reset clarification and comment
    setValue('kkb.clarification', null, {
      shouldDirty: true,
      shouldValidate: true,
    });

    setValue('kkb.comment', '', {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [setValue, kkbHazardous, prevKkbHazardous]);

  return (
    <FormProvider {...form}>
      <form id={formId} onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <I18nOptionsAutocompleteFieldWithController
          variant="standard"
          label={t('labels.orderType')}
          name="orderType"
          control={form.control}
          options={OrderTypeValues}
          i18n={{ ns: I18nNamespace.intake, keyPrefix: 'IntakeNuclearComplianceForm.OrderType' }}
          disabled={true}
          required={true}
        />

        <I18nOptionsAutocompleteFieldWithController
          variant="standard"
          label={t('labels.qGroup')}
          name="qGroup"
          control={form.control}
          options={QGroupValues}
          i18n={{ ns: I18nNamespace.intake, keyPrefix: 'IntakeNuclearComplianceForm.QGroup' }}
          required={true}
        />

        <YesNoRadioFieldWithController
          control={form.control}
          name="validationByProcurement"
          label={t('labels.validationByProcurement')}
          required={true}
        />

        {/* GKG form section */}
        <FormSection>
          <YesNoRadioFieldWithController
            control={form.control}
            name="gkg.required"
            label={t('labels.gkg.required')}
            required={true}
          />

          {gkgRequired !== null && (
            <I18nOptionsAutocompleteFieldWithController
              variant="standard"
              label={t('labels.gkg.measures')}
              name="gkg.measures"
              control={form.control}
              options={gkgRequired === true ? GkgYesMeasureValues : GkgNoMeasureValues}
              i18n={{
                ns: I18nNamespace.intake,
                keyPrefix:
                  gkgRequired === true
                    ? 'IntakeNuclearComplianceForm.GkgYesMeasures'
                    : 'IntakeNuclearComplianceForm.GkgNoMeasures',
              }}
              required={true}
            />
          )}

          <TextAreaFieldWithController
            control={form.control}
            name="gkg.comment"
            label={t('labels.gkg.comment')}
            fullWidth
            variant="standard"
            margin="dense"
          />
        </FormSection>

        {/* NDA form section */}
        <FormSection>
          <YesNoRadioFieldWithController
            control={form.control}
            name="nda.required"
            label={t('labels.nda.required')}
            required
          />

          {ndaRequired === true && (
            <>
              <I18nOptionsAutocompleteFieldWithController
                variant="standard"
                label={t('labels.nda.available')}
                name="nda.availability"
                control={form.control}
                options={NdaAvailabilityValues}
                i18n={{ ns: I18nNamespace.intake, keyPrefix: 'IntakeNuclearComplianceForm.NdaAvailability' }}
                required={true}
              />
            </>
          )}
        </FormSection>

        {/* KKB form section */}
        <FormSection>
          <YesNoRadioFieldWithController
            control={form.control}
            name="kkb.applicable"
            label={t('labels.kkb.applicable')}
            required
          />

          {kkbApplicable === true && (
            <>
              <YesNoRadioFieldWithController
                control={form.control}
                name="kkb.controlledZone"
                label={t('labels.kkb.controlledZone')}
                required
              />

              <YesNoRadioFieldWithController
                control={form.control}
                name="kkb.tenuFourZone"
                label={t('labels.kkb.tenuFourZone')}
                required
              />

              <YesNoRadioFieldWithController
                control={form.control}
                name="kkb.hazardous"
                label={t('labels.kkb.hazardous')}
                required
              />

              {kkbHazardous === true && (
                <>
                  <I18nOptionsAutocompleteFieldWithController
                    variant="standard"
                    label={t('labels.kkb.clarification')}
                    name="kkb.clarification"
                    control={form.control}
                    options={KkbClarificationValues}
                    i18n={{ ns: I18nNamespace.intake, keyPrefix: 'IntakeNuclearComplianceForm.KkbClarification' }}
                    required={true}
                  />

                  <TextAreaFieldWithController
                    control={form.control}
                    name="kkb.comment"
                    label={t('labels.kkb.comment')}
                    fullWidth
                    variant="standard"
                    margin="dense"
                  />
                </>
              )}
            </>
          )}
        </FormSection>
      </form>
    </FormProvider>
  );
};

export const FormSection = (props: PropsWithChildren): ReactNode => {
  return <div className="bg-neutral-50 rounded-md p-4 flex flex-col gap-2">{props.children}</div>;
};

// usePrevious hook to get the previous value of a variable
// needed to avoid running useEffect on initial render
function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T | undefined>(undefined);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}
